import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { FiRotateCw, FiTrash2, FiEdit3 } from "react-icons/fi";
import Table from "../shared/table/Table";
import { toast, ToastContainer } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { Link } from "react-router-dom";

import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";
import usePermission from "../../hooks/usePermission";

import "react-toastify/dist/ReactToastify.css";
import "react-confirm-alert/src/react-confirm-alert.css";

const TrashMailTemplates = () => {
  const BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/mail-templates`;

  const [trashTemplates, setTrashTemplates] = useState([]);
  const { canEdit, canDelete } = usePermission("static_mail_template");

  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const getText = (key, def) => translations[key] || def;

  useEffect(() => {
    fetchTrashedTemplates();
  }, []);

  const fetchTrashedTemplates = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const res = await axios.get(`${BASE_URL}/trash/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrashTemplates(res.data || []);
    } catch (error) {
      console.error("Çöp kutusu verisi alınamadı:", error);
      toast.error(getText("ERROR_TRASH_TEMPLATES_NOT_LOADED", "Çöp kutusu verisi alınamadı."));
    }
  };

  const handleRestore = (id) => {
    confirmAlert({
      title: getText("RESTORE_CONFIRM_TITLE", "Şablonu Geri Yükle"),
      message: getText("RESTORE_CONFIRM_MSG", "Bu mail şablonunu geri yüklemek istediğinize emin misiniz?"),
      buttons: [
        {
          label: getText("YES_RESTORE", "Evet, Geri Yükle"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.put(`${BASE_URL}/restore/${id}`, {}, {
                headers: { Authorization: `Bearer ${token}` },
              });
              toast.success(getText("SUCCESS_TEMPLATE_RESTORED", "Şablon başarıyla geri yüklendi!"));
              setTrashTemplates(prev => prev.filter(item => item.id !== id));
            } catch (error) {
              console.error("Geri yükleme hatası:", error);
              toast.error(getText("ERROR_TEMPLATE_RESTORE_FAILED", "Geri yükleme başarısız."));
            }
          },
        },
        {
          label: getText("CANCEL", "İptal"),
          onClick: () => toast.info(getText("RESTORE_CANCELLED", "İşlem iptal edildi.")),
        }
      ]
    });
  };
  
  // 3) Permanent Delete (Kalıcı Sil)
  const handlePermanentDelete = (id) => {
    confirmAlert({
      title: getText("DELETE_CONFIRM_TITLE", "Kalıcı Sil"),
      message: getText("DELETE_CONFIRM_MSG", "Bu şablonu kalıcı olarak silmek istediğinize emin misiniz?"),
      buttons: [
        {
          label: getText("YES_DELETE", "Evet, Sil"),
          onClick: async () => {
            try {
              const token = localStorage.getItem("admin_token");
              await axios.delete(`${BASE_URL}/permanent-delete/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              toast.success(getText("SUCCESS_TEMPLATE_DELETED", "Şablon kalıcı olarak silindi!"));
              setTrashTemplates(prev => prev.filter(item => item.id !== id));
            } catch (error) {
              console.error("Kalıcı silme hatası:", error);
              toast.error(getText("ERROR_TEMPLATE_DELETE_FAILED", "Kalıcı silme başarısız."));
            }
          }
        },
        {
          label: getText("CANCEL", "İptal"),
          onClick: () => toast.info(getText("DELETE_CANCELLED", "İşlem iptal edildi.")),
        }
      ]
    });
  };

  const columns = useMemo(() => [
    { accessorKey: "id", header: getText("TABLE_ID_LABEL", "ID"), size: 60 },
    { accessorKey: "title", header: getText("TABLE_TITLE_LABEL", "Başlık"), size: 250 },
    {
      accessorKey: "createdAt",
      header: getText("TABLE_CREATED_AT", "Oluşturulma Tarihi"),
      meta: { headerClassName: "text-end", className: "text-end" },
      size: 140,
      cell: (info) => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleDateString("tr-TR") : "-";
      }
    },
    {
      accessorKey: "deleted_at",
      header: getText("TABLE_DELETED_AT", "Silinme Tarihi"),
      meta: { headerClassName: "text-end", className: "text-end" },
      size: 140,
      cell: (info) => {
        const date = info.getValue();
        return date ? new Date(date).toLocaleDateString("tr-TR") : "-";
      }
    },
    {
      accessorKey: "actions",
      header: getText("TABLE_ACTIONS_LABEL", "İşlemler"),
      enableSorting: false,
      meta: { headerClassName: "text-end", className: "text-end" },
      size: 160,
      cell: (info) => {
        const id = info.row.original.id;
        return (
          <div className="d-flex gap-2 justify-content-end">
            {canEdit && (
                          <Link
                            to={`/mail-templates/edit/${id}`}
                            className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
                            title={getText("EDIT","Düzenle")}
                          >
                            <FiEdit3/>
                          </Link>
                        )}
            {canEdit && (
              <button
                onClick={() => handleRestore(id)}
                className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
                title={getText("RESTORE", "Geri Yükle")}
              >
                <FiRotateCw />
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => handlePermanentDelete(id)}
                className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
                title={getText("DELETE_PERMANENT", "Kalıcı Sil")}
              >
                <FiTrash2 />
              </button>
            )}
          </div>
        );
      }
    }
  ], [translations, canEdit, canDelete]);

  return (
    <div className="container mt-2">
      <ToastContainer />
      <Table data={trashTemplates} columns={columns} />
    </div>
  );
};

export default TrashMailTemplates;
